/** The recommendation board's reducer: actionability from the server clock, truthful probabilities (UX01, UX07). */
import type { BoardRow } from "../../contracts/views.ts";
import { PROBABILITY_SCALE } from "../../contracts/constants.ts";
import { formatDecimal } from "../../app/format.ts";
import { isExpired, secondsLeft, type Mono, type ServerClock } from "../../app/time.ts";

export interface PlanView {
  readonly row: BoardRow;
  readonly actionable: boolean;
  /** Why the plan cannot be acted on (empty when actionable). */
  readonly blockedBy: readonly string[];
  /** The remaining seconds on the server's clock, or 0. */
  readonly secondsLeft: number;
}

/** Decide whether a plan may be acted on: active, unexpired on the server clock, allocated, fresh data. */
export function evaluatePlan(
  row: BoardRow,
  clock: ServerClock,
  mono: Mono,
  actionsEnabled: boolean,
): PlanView {
  const blockedBy: string[] = [];
  if (!actionsEnabled) {
    blockedBy.push("data is stale or superseded; actions are disabled");
  }
  if (row.status === "EXPIRED") {
    blockedBy.push("the plan is expired");
  }
  if (row.status === "INVALIDATED") {
    blockedBy.push("the plan is invalidated");
  }
  if (row.status === "SUPERSEDED" || row.supersededBy !== null) {
    blockedBy.push(`superseded by ${row.supersededBy ?? "a newer version"}`);
  }
  if (row.status === "ACTIVE" && isExpired(row.expiresAt, clock, mono)) {
    blockedBy.push("the server clock says the plan has expired");
  }
  if (row.allocation !== "ALLOCATED") {
    blockedBy.push(`allocation is ${row.allocation}`);
  }
  if (row.plan === null) {
    blockedBy.push("no complete plan exists");
  }
  return {
    row,
    actionable: blockedBy.length === 0,
    blockedBy,
    secondsLeft: secondsLeft(row.expiresAt, clock, mono),
  };
}

export interface ProbabilityView {
  /** The calibrated probability, or `null`: never an uncalibrated score. */
  readonly text: string | null;
  readonly note: string;
}

/** The only place a probability is produced: calibrated and present, else null with a reason. */
export function probabilityView(row: BoardRow): ProbabilityView {
  if (row.calibrationStatus === "CALIBRATED" && row.probability !== null) {
    return {
      text: formatDecimal(row.probability, PROBABILITY_SCALE),
      note: "calibrated probability",
    };
  }
  if (row.probability !== null) {
    return { text: null, note: "an uncalibrated value was withheld; it is not a probability" };
  }
  return {
    text: null,
    note: row.probabilityNote ?? `probability ${row.calibrationStatus.toLowerCase()}`,
  };
}

/** Order for display: by rank, then listing (ordinal rank is not a probability). */
export function byRank(rows: readonly BoardRow[]): BoardRow[] {
  return [...rows].sort((a, b) => a.rank - b.rank || a.listing.localeCompare(b.listing));
}

/** A URL is shown as a link only when it is https; external content is otherwise text. */
export function safeHref(url: string): string | null {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" ? parsed.href : null;
  } catch {
    return null;
  }
}
